/**
 * Upsell Stats storefront tracker.
 *
 * Records four things about every upsell placement: it was seen, it was
 * engaged with, it was added to cart, it was dismissed. Adds are the only ones
 * revenue attribution depends on; the rest are what make the funnel readable.
 *
 * Lives in the theme rather than in the app's theme extension, following the
 * "Adding JS" pattern in this repo's CLAUDE.md. That means it ships when the
 * theme ships, with no separate app deploy and no app embed for someone to
 * leave switched off. The app still owns the /apps/upsell-stats proxy, the
 * order webhooks, the database and the dashboard; only this file moved.
 *
 * Standalone and un-minified on purpose. It has no .min twin, so unlike
 * global.js there is only one copy to edit.
 *
 * Three design decisions worth knowing before changing anything here.
 *
 * 1. THE MARKUP CONTRACT IS GENERIC. Nothing in this file knows about any
 *    particular section. A placement is any element carrying
 *    `data-upsell-surface`, and the product it offers is `data-upsell-product`.
 *    A new placement is two attributes, with no change to this file.
 *
 * 2. ADDS ARE OBSERVED, NOT INFERRED FROM CLICKS. A click on an add button is
 *    not an add: the request can fail, the variant can be sold out, the shopper
 *    can be adding the main product rather than the upsell. So the tracker
 *    watches `/cart/add.js` responses and reads what Shopify says was actually
 *    added. Counting clicks would overstate every take rate on the dashboard.
 *
 * 3. IDENTITY TRAVELS IN THE BODY. Shopify's app proxy strips the Cookie
 *    header before forwarding, so the server cannot read the cart token or
 *    anything else client-side. Every identifier is read here and posted in
 *    the payload. This is not a shortcut; it is the only channel that exists.
 */
(function () {
  'use strict'

  // Config rides on the script tag's own data attributes, so the theme needs
  // one line in theme.liquid instead of a separate inline JSON block. The
  // session hash is the only value that has to come from Liquid.
  var tag = document.getElementById('upsell-stats-tracker')
  if (!tag) return

  var config = {
    endpoint: (tag.getAttribute('data-proxy') || '/apps/upsell-stats') + '/event',
    sessionId: tag.getAttribute('data-session') || '',
    debug: tag.getAttribute('data-debug') === 'true',
  }

  var DEBUG = !!config.debug
  // Time an upsell must be at least half visible before it counts as seen.
  // Stops one scrolled past at speed from inflating impressions.
  var DWELL_MS = 400
  var FLUSH_MS = 4000
  var MAX_BATCH = 20
  // Consecutive send failures before this page gives up. Without it, a theme
  // still carrying this file after the app is uninstalled would retry against
  // a dead proxy for the whole session on every page.
  var MAX_FAILURES = 3
  var failures = 0
  // How long a placement keeps credit for a product after it was shown. Covers
  // the shopper who opens the drawer, navigates, and adds from the next page.
  var TOUCH_TTL_MS = 30 * 60 * 1000
  var VISITOR_KEY = '_us_vid'
  var TOUCH_KEY = '_us_touches'
  var NAV_KEY = '_us_nav'
  var WATCH_KEY = 'wb:watch'

  // Report a failure to the WristBuddys storefront logging that
  // snippets/storefront-logs.liquid installs. Guarded, because that snippet is
  // a separate concern and may not be present.
  //
  // Only failures that mean "the analytics stopped" are reported, and only
  // once each. slog already caps a page at five events and collapses repeats,
  // but a tracker that reported every send failure would spend that whole
  // budget and crowd out the errors that say the page itself is broken.
  var reported = {}
  function reportFailure(reason, fields) {
    if (reported[reason]) return
    reported[reason] = 1
    try {
      if (window.slog) {
        window.slog('upsell_tracker_error', {
          message: reason,
          extra: fields || {},
        })
      }
    } catch (e) {
      /* logging must never be the thing that breaks */
    }
  }

  function log() {
    if (!DEBUG) return
    try {
      console.log.apply(console, ['[upsell-stats]'].concat([].slice.call(arguments)))
    } catch (e) {
      /* console can be absent in some in-app browsers */
    }
  }

  // ── Identity ──────────────────────────────────────────────────────────────

  function readCookie(name) {
    var m = document.cookie.match('(?:^|;\\s*)' + name + '=([^;]*)')
    return m ? decodeURIComponent(m[1]) : null
  }

  function uuid() {
    try {
      if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID()
    } catch (e) {
      /* fall through */
    }
    // Not cryptographically meaningful, and it does not need to be: this is an
    // anonymous key for joining a shopper's own events together.
    return 'u-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10)
  }

  var visitorId = null
  try {
    visitorId = localStorage.getItem(VISITOR_KEY)
    if (!visitorId) {
      visitorId = uuid()
      localStorage.setItem(VISITOR_KEY, visitorId)
    }
  } catch (e) {
    // Private mode, or storage disabled. Fall back to a per-page id: the
    // cart token still carries attribution across pages.
    visitorId = uuid()
  }

  function cartToken() {
    return readCookie('cart')
  }

  // request.session_hash renders empty in some contexts, the theme dev server
  // among them, so fall back to Shopify's own session cookie. Attribution
  // prefers the cart token anyway; this is the third of three identifiers.
  if (!config.sessionId) {
    config.sessionId = readCookie('_shopify_s') || readCookie('_session_id') || ''
  }

  // Simple Split Testing writes its assignments to `splt_assignments` as
  // URI-encoded JSON. Reading it here rather than asking that app's backend
  // keeps the two decoupled: this app never buckets anyone, it just reports
  // the arm the shopper was already in, so the two can never disagree.
  var splitCache = null
  function splitAssignments() {
    // Parsed once per page. This used to run on every intersection entry and
    // every click, doing a cookie regex and a JSON.parse each time, which is
    // pure waste: the cookie cannot meaningfully change mid-page.
    if (splitCache !== null) return splitCache
    try {
      var raw = readCookie('splt_assignments')
      var parsed = raw ? JSON.parse(raw) : null
      splitCache = (parsed && parsed.assignments) || []
    } catch (e) {
      splitCache = []
    }
    return splitCache
  }

  function splitArm(preferredTestId) {
    try {
      var list = splitAssignments()
      if (!list || !list.length) return null
      if (preferredTestId) {
        for (var i = 0; i < list.length; i++) {
          var a = list[i]
          if (a.splitTestId === preferredTestId || a.splitTestHandle === preferredTestId) {
            return { testId: a.splitTestId, variantId: (a.variant && a.variant.handle) || (a.variant && a.variant.id) || '' }
          }
        }
        return null
      }
      // With several tests running and no hint on the element, the first
      // assignment is used. A placement that belongs to a specific test should
      // say so with data-upsell-split-test.
      var first = list[0]
      return {
        testId: first.splitTestId,
        variantId: (first.variant && first.variant.handle) || (first.variant && first.variant.id) || '',
      }
    } catch (e) {
      return null
    }
  }

  // ── Recent touches ────────────────────────────────────────────────────────
  // Which placement showed which product, kept across page views so an add
  // that happens after navigation still credits the right one.

  function loadTouches() {
    try {
      var raw = sessionStorage.getItem(TOUCH_KEY)
      if (!raw) return {}
      var parsed = JSON.parse(raw)
      var now = Date.now()
      var out = {}
      for (var key in parsed) {
        if (parsed[key] && now - parsed[key].ts < TOUCH_TTL_MS) out[key] = parsed[key]
      }
      return out
    } catch (e) {
      return {}
    }
  }

  var touches = loadTouches()

  function rememberTouch(productId, surface, splitTestId, splitVariantId) {
    if (!productId) return
    touches[String(productId)] = {
      surface: surface,
      ts: Date.now(),
      t: splitTestId || null,
      v: splitVariantId || null,
    }
    scheduleTouchSave()
  }

  // sessionStorage writes are synchronous and this used to stringify the whole
  // map on every single impression. Coalesced into one write per idle slot, so
  // a page revealing a dozen placements at once pays for one serialisation
  // rather than twelve.
  var touchSaveScheduled = false
  function scheduleTouchSave() {
    if (touchSaveScheduled) return
    touchSaveScheduled = true
    defer(function () {
      touchSaveScheduled = false
      try {
        sessionStorage.setItem(TOUCH_KEY, JSON.stringify(touches))
      } catch (e) {
        /* storage full or disabled; in-memory copy still works for this page */
      }
    })
  }

  function recallTouch(productId) {
    if (!productId) return null
    var hit = touches[String(productId)]
    if (!hit) return null
    if (Date.now() - hit.ts > TOUCH_TTL_MS) return null
    return hit
  }

  // ── The shopper's watch ───────────────────────────────────────────────────
  // Written by the strap finder (assets/section-strap-finder.js) when the
  // shopper picks a brand and model, as localStorage `wb:watch`, and announced
  // on the window as a `wb:watch` event. Carried here onto every event and,
  // as a cart attribute, onto the order. It is the only stated watch the store
  // ever gets; everything else the app knows about watches is inferred from
  // what was bought.

  var watch = null
  var watchReference = ''

  function readWatch() {
    try {
      var raw = localStorage.getItem(WATCH_KEY)
      if (!raw) return null
      var parsed = JSON.parse(raw)
      if (!parsed || !parsed.brand) return null
      watchReference = String(parsed.reference || '').slice(0, 64)
      var key = String(parsed.brand) + (parsed.model ? ' / ' + String(parsed.model) : '')
      return key.slice(0, 64)
    } catch (e) {
      return null
    }
  }

  watch = readWatch()

  window.addEventListener('wb:watch', function () {
    var next = readWatch()
    if (next === watch) return
    watch = next
    // A new answer goes onto the cart straight away if the app has said the
    // attribute may be written; otherwise the next batch response triggers it.
    cartAttributeWritten = false
    maybeWriteCartAttribute()
  })

  // ── Sibling clicks ────────────────────────────────────────────────────────
  // Which product page the shopper was on when they clicked through to which
  // other product. Kept apart from the upsell touches above on purpose: a
  // sibling tile is navigation between two straps, and letting it into the
  // touch map would make every strap bought after one count as an upsell.
  //
  // Remembered per destination so a later add of that product can carry the
  // page it was chosen from. Same storage and TTL as the upsell touches.

  function loadNav() {
    try {
      var raw = sessionStorage.getItem(NAV_KEY)
      if (!raw) return {}
      var parsed = JSON.parse(raw)
      var now = Date.now()
      var out = {}
      for (var key in parsed) {
        if (parsed[key] && now - parsed[key].ts < TOUCH_TTL_MS) out[key] = parsed[key]
      }
      return out
    } catch (e) {
      return {}
    }
  }

  var navTouches = loadNav()
  var navSaveScheduled = false

  // The watch the current chain of sibling clicks is for: the last
  // watch-specific (curved) page the shopper was on since this page loaded.
  // Sibling clicks swap the product in place, so this survives them; a real
  // page load starts a new chain, which is the point. A shopper who lands
  // on a Submariner strap and clicks through two generic straps is still
  // shopping for a Submariner. One who leaves and opens a Speedmaster page
  // is not.
  var chainOrigin = ''

  // Only a listing made for one watch starts or moves a chain: curved, and
  // its models metafield names exactly one model. A curved strap that fits
  // every MoonSwatch mission is as general as a universal strap, so passing
  // through it on the way leaves the chain credited to the Mercury listing
  // the shopper started on. A tile from a page cached before the models
  // attribute existed falls back to curved alone.
  function noteChainPage(productId, kind, models) {
    if (!productId || kind !== 'curved') return
    if (models !== null && models !== undefined && models !== '' && Number(models) !== 1) return
    chainOrigin = String(productId)
  }

  function rememberNav(to, from, surface, origin) {
    if (!to || !from) return
    navTouches[String(to)] = { from: String(from), surface: surface, origin: origin || '', ts: Date.now() }
    if (navSaveScheduled) return
    navSaveScheduled = true
    defer(function () {
      navSaveScheduled = false
      try {
        sessionStorage.setItem(NAV_KEY, JSON.stringify(navTouches))
      } catch (e) {
        /* storage full or disabled; in-memory copy still works for this page */
      }
    })
  }

  function recallNav(to) {
    if (!to) return null
    var hit = navTouches[String(to)]
    if (!hit) return null
    if (Date.now() - hit.ts > TOUCH_TTL_MS) return null
    return hit
  }

  function navOf(el) {
    var node = el && el.closest ? el.closest('[data-upsell-nav]') : null
    if (!node) return null
    var surface = node.getAttribute('data-upsell-nav')
    var from = node.getAttribute('data-upsell-nav-from') || ''
    if (!surface || !from) return null
    return {
      node: node,
      surface: surface,
      from: from,
      fromKind: node.getAttribute('data-upsell-nav-from-kind') || '',
      fromModels: node.getAttribute('data-upsell-nav-from-models'),
      to: node.getAttribute('data-upsell-nav-to') || '',
      variantId: node.getAttribute('data-upsell-nav-variant') || '',
    }
  }

  function navEvent(type, surface, from, to, variantId, origin) {
    var e = { __nav: true, type: type, surface: surface, from: from, visitorId: visitorId }
    if (to) e.to = to
    if (variantId) e.variantId = variantId
    if (origin) e.origin = origin
    if (watch) e.watch = watch
    var ct = cartToken()
    if (ct) e.cartToken = ct
    if (config.sessionId) e.sessionId = config.sessionId
    return e
  }

  // ── Hotjar ───────────────────────────────────────────────────────────────────────
  // Names the session in Hotjar so a recording can be found by what the
  // shopper did with an upsell, rather than one id at a time.
  //
  // The theme's own Hotjar loader is consent gated: until the shopper accepts
  // analytics cookies `window.hj` does not exist. It drains a localStorage
  // queue when it finally starts, so an event fired before consent survives.
  // Mirroring that contract here means this file never has to know whether
  // Hotjar is up yet.
  //
  // Clicks and adds only. An impression fires on nearly every page view and
  // would drown the filter that makes this worth having, which is "show me the
  // sessions where somebody actually took an upsell".
  var HJ_QUEUE_KEY = 'wb_hj_queue'
  var HJ_QUEUE_MAX = 20
  var hjSent = Object.create(null)

  function hotjarEventName(type, surface) {
    return ('upsell_' + type + '_' + String(surface || 'unknown'))
      .toLowerCase()
      .replace(/[^a-z0-9_]+/g, '_')
      .slice(0, 60)
  }

  function hotjar(type, surface) {
    try {
      var name = hotjarEventName(type, surface)
      // Once per page per name. A bundle add calls recordAdd once per add-on
      // line, and three identical events help nobody.
      if (hjSent[name]) return
      hjSent[name] = true

      if (typeof window.hj === 'function') {
        window.hj('event', name)
        return
      }

      var raw = localStorage.getItem(HJ_QUEUE_KEY)
      var queued = raw ? JSON.parse(raw) : []
      if (!Array.isArray(queued)) queued = []
      if (queued.length >= HJ_QUEUE_MAX) return
      for (var i = 0; i < queued.length; i++) {
        if (queued[i] && queued[i].name === name) return
      }
      queued.push({ name: name })
      localStorage.setItem(HJ_QUEUE_KEY, JSON.stringify(queued))
    } catch (e) {
      /* Hotjar is a nice-to-have and must never affect the tracker */
    }
  }

  // ── Queue ─────────────────────────────────────────────────────────────────

  var queue = []
  var flushTimer = null
  var lastError = null
  var cartAttributeAllowed = false
  var cartAttributeWritten = false

  function enqueue(event) {
    event.id = uuid()
    event.ts = Date.now()
    queue.push(event)
    log('queued', event)
    if (queue.length >= MAX_BATCH) {
      flush(false)
    } else if (!flushTimer) {
      flushTimer = setTimeout(function () {
        flushTimer = null
        flush(false)
      }, FLUSH_MS)
    }
  }

  // One queue, two arrays on the wire. Add-to-cart routes are not placements
  // and the endpoint keeps them apart, but sharing the queue means they also
  // share the batching, the retry, the beacon on pagehide and the circuit
  // breaker, none of which is worth writing twice.
  function payload(batch) {
    var events = []
    var atc = []
    var nav = []
    for (var i = 0; i < batch.length; i++) {
      var e = batch[i]
      if (!e.__atc && !e.__nav) {
        events.push(e)
        continue
      }
      var copy = {}
      for (var k in e) {
        if (k !== '__atc' && k !== '__nav' && Object.prototype.hasOwnProperty.call(e, k)) copy[k] = e[k]
      }
      if (e.__nav) nav.push(copy)
      else atc.push(copy)
    }
    return JSON.stringify({
      events: events,
      atc: atc,
      nav: nav,
      page: location.pathname + location.search,
    })
  }

  function flush(useBeacon) {
    // Already given up for this page. The report happens where the breaker
    // trips, not here: after the final failure nothing calls flush again, so
    // reporting from this guard meant the report never fired at all.
    if (failures >= MAX_FAILURES) {
      queue.length = 0
      return
    }
    if (flushTimer) {
      clearTimeout(flushTimer)
      flushTimer = null
    }
    if (!queue.length) return
    var batch = queue.splice(0, MAX_BATCH)
    var body = payload(batch)

    // On pagehide the document is going away and a fetch would be cancelled
    // mid-flight. sendBeacon is the only transport the browser promises to
    // finish, and it is fire-and-forget, so there is no retry to attempt.
    if (useBeacon && navigator.sendBeacon) {
      try {
        var blob = new Blob([body], { type: 'application/json' })
        navigator.sendBeacon(config.endpoint, blob)
        log('beacon', batch.length)
        return
      } catch (e) {
        /* fall through to fetch */
      }
    }

    fetch(config.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body,
      keepalive: true,
      credentials: 'same-origin',
    })
      .then(function (res) {
        if (!res.ok) throw new Error('ingest ' + res.status)
        return res.json().catch(function () {
          return null
        })
      })
      .then(function (data) {
        failures = 0
        log('sent', batch.length, data)
        if (data && data.cartAttribute) {
          cartAttributeAllowed = true
          maybeWriteCartAttribute()
        }
      })
      .catch(function (err) {
        failures += 1
        lastError = err && err.message ? err.message : err
        log('send failed', err, 'failures=' + failures)
        // Put the batch back at the front and schedule another attempt. Every
        // event carries a stable id, so the server drops it if the first
        // attempt actually landed and only the response was lost.
        //
        // The retry has to be scheduled explicitly. Without it the requeued
        // batch sat until the next impression happened to arrive, so on a quiet
        // page the failure count never reached the circuit breaker and nothing
        // was ever reported. MAX_FAILURES is what stops this looping.
        queue = batch.concat(queue)
        if (failures >= MAX_FAILURES) {
          // Giving up for this page. The proxy is not answering, which usually
          // means the app was uninstalled, its proxy path changed, or the
          // worker is down. Worth reporting once, because the only other
          // symptom is a dashboard that quietly stops filling in.
          reportFailure('upsell ingest unreachable', {
            endpoint: config.endpoint,
            failures: failures,
            last_error: String(lastError || '').slice(0, 200),
            dropped: queue.length,
          })
          queue.length = 0
          return
        }
        if (!flushTimer) {
          flushTimer = setTimeout(function () {
            flushTimer = null
            flush(false)
          }, FLUSH_MS)
        }
      })
  }

  // ── Cart attribute ────────────────────────────────────────────────────────
  // Shopify's cart token can change between the add and the order in some
  // accelerated checkout flows. A hidden cart attribute survives that. It is
  // written at most once per page and only when the app says the merchant has
  // it switched on.

  function maybeWriteCartAttribute() {
    if (!cartAttributeAllowed || cartAttributeWritten) return
    cartAttributeWritten = true
    var attributes = { _us_vid: visitorId }
    if (config.sessionId) attributes._us_sid = config.sessionId
    if (watch) {
      attributes._watch = watch
      attributes._watch_reference = watchReference
    }
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes }),
      credentials: 'same-origin',
    })
      .then(function () {
        log('cart attribute written')
      })
      .catch(function (err) {
        log('cart attribute failed', err)
      })
  }

  // ── Reading a placement off the DOM ───────────────────────────────────────

  function surfaceOf(el) {
    var node = el && el.closest ? el.closest('[data-upsell-surface]') : null
    if (!node) return null
    var key = node.getAttribute('data-upsell-surface')
    if (!key) return null
    var arm = splitArm(node.getAttribute('data-upsell-split-test'))
    return {
      node: node,
      surface: key,
      productId: node.getAttribute('data-upsell-product') || '',
      variantId: node.getAttribute('data-upsell-variant') || '',
      splitTestId: arm ? arm.testId : '',
      splitVariantId: arm ? arm.variantId : '',
    }
  }

  function baseEvent(info, type) {
    var e = {
      type: type,
      surface: info.surface,
      visitorId: visitorId,
    }
    if (info.productId) e.productId = info.productId
    if (info.variantId) e.variantId = info.variantId
    if (info.splitTestId) e.splitTestId = info.splitTestId
    if (info.splitVariantId) e.splitVariantId = info.splitVariantId
    if (watch) e.watch = watch
    var ct = cartToken()
    if (ct) e.cartToken = ct
    if (config.sessionId) e.sessionId = config.sessionId
    return e
  }

  // Each (surface, product, type) fires once per page view. Without this an
  // upsell in a sticky drawer would report an impression on every scroll that
  // re-intersected it, and the take rate would fall towards zero as a
  // shopper simply spent longer on the page.
  var firedThisPage = Object.create(null)

  function fireOnce(info, type) {
    var key = info.surface + '|' + info.productId + '|' + type
    if (firedThisPage[key]) return
    firedThisPage[key] = true
    enqueue(baseEvent(info, type))
  }

  // ── Impressions ───────────────────────────────────────────────────────────

  var dwellTimers = new WeakMap()

  var observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              var nav = entry.target.hasAttribute('data-upsell-nav') ? navOf(entry.target) : null
              var info = nav ? null : surfaceOf(entry.target)
              if (!info && !nav) return
              if (entry.isIntersecting) {
                if (dwellTimers.has(entry.target)) return
                var timer = setTimeout(function () {
                  dwellTimers.delete(entry.target)
                  if (nav) {
                    // Seeing the row on a watch-specific page makes that page
                    // the chain's origin, click or no click.
                    noteChainPage(nav.from, nav.fromKind, nav.fromModels)
                    // The row, not the tile: one view per source page per
                    // page view, however many tiles scroll in.
                    var navKey = 'nav|' + nav.surface + '|' + nav.from + '|view'
                    if (firedThisPage[navKey]) return
                    firedThisPage[navKey] = true
                    enqueue(navEvent('view', nav.surface, nav.from, '', ''))
                    return
                  }
                  fireOnce(info, 'impression')
                  rememberTouch(info.productId, info.surface, info.splitTestId, info.splitVariantId)
                }, DWELL_MS)
                dwellTimers.set(entry.target, timer)
              } else {
                var pending = dwellTimers.get(entry.target)
                if (pending) {
                  clearTimeout(pending)
                  dwellTimers.delete(entry.target)
                }
              }
            })
          },
          { threshold: 0.5 },
        )
      : null

  function observeAll(root) {
    if (!observer) return
    var scope = root && root.querySelectorAll ? root : document
    var nodes = scope.querySelectorAll('[data-upsell-surface], [data-upsell-nav]')
    for (var i = 0; i < nodes.length; i++) observer.observe(nodes[i])
  }

  // Upsell drawers and cart sections are rendered after the fact, so the
  // initial pass would miss almost everything that matters.
  // Runs work when the browser is idle, falling back to a short timeout in the
  // in-app browsers that lack requestIdleCallback. Never rAF: this is
  // bookkeeping, and rAF would put it on the frame-critical path.
  function defer(fn) {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(fn, { timeout: 500 })
    } else {
      setTimeout(fn, 0)
    }
  }

  // A mutation anywhere on the page used to run querySelectorAll against every
  // added node, so one cart re-render meant dozens of redundant queries on the
  // main thread. Now any burst of mutations sets a flag and one scan runs when
  // the browser is next idle. observe() on an already-observed element is a
  // no-op, so rescanning the document is cheaper than tracking what is new.
  var rescanScheduled = false
  function scheduleRescan() {
    if (rescanScheduled) return
    rescanScheduled = true
    defer(function () {
      rescanScheduled = false
      observeAll(document)
    })
  }

  var mutationObserver =
    'MutationObserver' in window
      ? new MutationObserver(function (mutations) {
          for (var i = 0; i < mutations.length; i++) {
            if (mutations[i].addedNodes.length > 0) {
              scheduleRescan()
              return
            }
          }
        })
      : null

  // ── Add-to-cart routes ────────────────────────────────────────────────────
  // Which control the shopper pressed, for every add on the store rather than
  // only the upsells.
  //
  // Nothing above can see this. `recordAdd` deliberately drops an add with no
  // touch and no bundle role, because an ordinary product add is not an upsell
  // and counting it as one would move every rate in the app. So the quick add
  // on a collection card, which is most of how this store is browsed, was
  // invisible. These go to their own array in the batch and their own table.
  //
  // The classification mirrors the one global.js already computes at the
  // moment of the click and keeps to itself. Order matters and matches that
  // file: an upsell card renders through product-card_form, so it carries the
  // quick add class too and has to be tested first.

  function atcSourceOf(el) {
    var button = el.closest('[data-action="add-to-cart"]')
    if (!button) return null

    // The sticky bar forwards to the real button with mainButton.click(), so
    // counting it here would count the same press twice, once as the sticky
    // and once as the product page. The forwarded click is the one that lands.
    if (button.closest('.pm__sticky-atc')) return null

    if (button.closest('.pm__upsell-card--template, .pm__upsell-card')) return 'upsell'
    // The class rather than data-quick-atc, which the theme only emits for
    // products with more than one variant. A single-variant card is still a
    // quick add and is exactly the case this is meant to count.
    if (button.classList.contains('product-card__instant-atc')) return 'quick_atc'
    if (button.closest('cart-drawer')) return 'cart_drawer'
    if (button.closest('product-component') && button.closest('.pm__product-atc-wrapper')) {
      return 'pdp_main'
    }
    return 'other'
  }

  // ── Simple Split Testing ──────────────────────────────────────────────────
  //
  // The same click, published again as a Shopify custom event for the other
  // app to pick up. Its tracking pixel subscribes to `all_events`, so anything
  // published here arrives there with no integration to build: it ties the
  // event to its own visitor record, and it already knows whether that visitor
  // went on to check out.
  //
  // That is the question this app cannot answer. A day-grain counter has no
  // one to follow to an order, and giving it one would mean rebuilding
  // visitor-to-purchase tracking that already exists next door.
  //
  // Only the two routes worth comparing against each other. Publishing all
  // five would put three more names in that app's event list that nobody has a
  // question about, and its funnel is keyed on the event name.
  var SPLIT_EVENT_NAMES = {
    quick_atc: 'quick_add_used',
    pdp_main: 'product_page_add_used',
  }

  // Coarse on purpose. This becomes a grouping dimension over there, and that
  // app caps a dimension at 200 distinct values a day before folding the rest
  // into an "other" bucket. Sending the raw path would spend the whole cap on
  // product handles and tell nobody anything.
  function pageKind() {
    var p = location.pathname
    if (p.indexOf('/products/') !== -1) return 'product'
    if (p.indexOf('/collections/') !== -1) return 'collection'
    if (p.indexOf('/search') !== -1) return 'search'
    if (p.indexOf('/cart') !== -1) return 'cart'
    // Bare root, or a locale root like /en-ca.
    if (/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/?)?$/i.test(p)) return 'home'
    return 'other'
  }

  function publishSplitEvent(source) {
    try {
      var name = SPLIT_EVENT_NAMES[source]
      if (!name) return
      if (!window.Shopify || !window.Shopify.analytics) return
      if (typeof window.Shopify.analytics.publish !== 'function') return
      window.Shopify.analytics.publish(name, { source: source, page_type: pageKind() })
    } catch (e) {
      /* analytics must never break add to cart */
    }
  }

  // Every path wrapped, not just the classification. This runs inside a
  // capture-phase listener on the add to cart button of a live store, so it
  // has exactly one hard requirement: it must never be able to interfere with
  // the shopper adding something to their cart.
  function recordAtcClick(target) {
    try {
      var source = atcSourceOf(target)
      if (!source) return

      publishSplitEvent(source)

      var event = { __atc: true, source: source, visitorId: visitorId }
      if (watch) event.watch = watch
      var card = target.closest('[data-upsell-product], [data-productid], [data-product-id]')
      if (card) {
        var productId =
          card.getAttribute('data-upsell-product') ||
          card.getAttribute('data-productid') ||
          card.getAttribute('data-product-id')
        if (productId) event.productId = String(productId).slice(0, 64)
      }
      var ct = cartToken()
      if (ct) event.cartToken = ct
      if (config.sessionId) event.sessionId = config.sessionId

      // Not flushed immediately. Unlike an add, nothing downstream is waiting
      // on this and the shopper is usually still on the page, so it rides the
      // normal batch timer rather than costing its own request.
      enqueue(event)
    } catch (e) {
      /* tracking must never break add to cart */
    }
  }

  // ── Clicks and dismissals ─────────────────────────────────────────────────

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target
      if (!target || !target.closest) return

      // Independent of the placement below: most adds on this store happen
      // nowhere near an upsell.
      recordAtcClick(target)

      // A sibling tile. Recorded and remembered so an add of the destination
      // product can say which page it was chosen from, then handled no
      // further: the tile is not an upsell placement.
      var nav = navOf(target)
      if (nav) {
        try {
          if (nav.to && nav.to !== nav.from) {
            noteChainPage(nav.from, nav.fromKind, nav.fromModels)
            var navKey = 'nav|' + nav.surface + '|' + nav.from + '|' + nav.to + '|click'
            if (!firedThisPage[navKey]) {
              firedThisPage[navKey] = true
              enqueue(navEvent('click', nav.surface, nav.from, nav.to, nav.variantId, chainOrigin))
            }
            rememberNav(nav.to, nav.from, nav.surface, chainOrigin)
          }
        } catch (e) {
          /* tracking must never break navigation */
        }
        return
      }

      var info = surfaceOf(target)
      if (!info) return

      if (target.closest('[data-upsell-dismiss]')) {
        fireOnce(info, 'dismiss')
        return
      }

      fireOnce(info, 'click')
      hotjar('click', info.surface)
      // A click is a strong hint that this placement is the one that will get
      // credit for whatever gets added next, so refresh the touch even if the
      // impression already recorded it.
      rememberTouch(info.productId, info.surface, info.splitTestId, info.splitVariantId)
    },
    true,
  )

  // ── Adds ──────────────────────────────────────────────────────────────────
  // Observed from what Shopify actually added, never inferred from a click.

  function itemsFromAddResponse(data) {
    if (!data) return []
    // /cart/add.js returns either the single added item or `{ items: [...] }`
    // depending on how the request was made. Both shapes are handled because
    // themes use both.
    if (Array.isArray(data.items)) return data.items
    if (data.id || data.product_id || data.variant_id) return [data]
    return []
  }

  function recordAdd(item) {
    if (!item) return
    var productId = item.product_id != null ? String(item.product_id) : ''
    var variantId = item.variant_id != null ? String(item.variant_id) : item.id != null ? String(item.id) : ''
    var props = item.properties || {}
    var bundleRole = props._bundle_role || null
    var bundleId = props._bundle_id || null

    // Independent of the upsell path below. A product reached through a
    // sibling tile is usually an ordinary add, which the upsell path drops on
    // purpose; this is the one place that add gets remembered.
    try {
      var navHit = recallNav(productId)
      if (navHit) {
        var navAdd = navEvent('add', navHit.surface, navHit.from, productId, variantId, navHit.origin)
        navAdd.id = uuid()
        navAdd.ts = Date.now()
        queue.push(navAdd)
        log('nav add', navAdd)
      }
    } catch (e) {
      /* tracking must never break add to cart */
    }

    var touch = recallTouch(productId)
    // An add with no touch and no bundle role is an ordinary product add, not
    // an upsell. Recording it would inflate every number in the app.
    if (!touch && bundleRole !== 'addon') {
      // The nav add above still has to go out; it may be the last thing this
      // page does before checkout.
      if (queue.length) flush(false)
      return
    }

    var event = {
      type: 'add',
      surface: touch ? touch.surface : 'unattributed',
      visitorId: visitorId,
      productId: productId,
    }
    if (variantId) event.variantId = variantId
    if (bundleId) event.bundleId = String(bundleId)
    if (watch) event.watch = watch
    var arm = touch && touch.t ? { testId: touch.t, variantId: touch.v } : splitArm(null)
    if (arm && arm.testId) {
      event.splitTestId = arm.testId
      event.splitVariantId = arm.variantId || ''
    }
    var ct = cartToken()
    if (ct) event.cartToken = ct
    if (config.sessionId) event.sessionId = config.sessionId

    event.id = uuid()
    event.ts = Date.now()
    queue.push(event)
    log('add', event)
    hotjar('add', event.surface)
    maybeWriteCartAttribute()
    // Adds are the events attribution depends on and the shopper may be about
    // to leave for checkout, so they go out immediately rather than waiting
    // for the batch timer.
    flush(false)
  }

  function isAddUrl(url) {
    if (!url) return false
    try {
      var path = String(url).split('?')[0]
      return path.indexOf('/cart/add') !== -1
    } catch (e) {
      return false
    }
  }

  // Wrapping fetch and XHR is the only way to see what a theme's add-to-cart
  // actually did. Both wrappers are strictly pass-through: they never block,
  // never alter the request or response, and every observation is inside a
  // try/catch, because a tracker must not be able to break a shop's checkout.
  if (window.fetch) {
    var nativeFetch = window.fetch
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : input && input.url
      var result = nativeFetch.apply(this, arguments)
      if (isAddUrl(url)) {
        result
          .then(function (res) {
            if (!res || !res.ok) return
            res
              .clone()
              .json()
              .then(function (data) {
                itemsFromAddResponse(data).forEach(recordAdd)
              })
              .catch(function (e) {
                // Both failure modes land here: a body that is not JSON, and
                // anything thrown while reading the items out of it. The
                // earlier version reported only the second, because a rejected
                // .json() skips the try/catch entirely and was swallowed by an
                // empty .catch. Either way it means Shopify's add response no
                // longer looks the way this expects, which is worth knowing.
                log('add parse failed', e)
                reportFailure('cart add response unreadable', {
                  err: String((e && e.message) || e).slice(0, 200),
                })
              })
          })
          .catch(function () {})
      }
      return result
    }
  }

  if (window.XMLHttpRequest) {
    var nativeOpen = XMLHttpRequest.prototype.open
    XMLHttpRequest.prototype.open = function (method, url) {
      try {
        this.__usIsAdd = isAddUrl(url)
      } catch (e) {
        /* ignore */
      }
      return nativeOpen.apply(this, arguments)
    }
    var nativeSend = XMLHttpRequest.prototype.send
    XMLHttpRequest.prototype.send = function () {
      var xhr = this
      if (xhr.__usIsAdd) {
        xhr.addEventListener('load', function () {
          try {
            if (xhr.status < 200 || xhr.status >= 300) return
            var data = JSON.parse(xhr.responseText)
            itemsFromAddResponse(data).forEach(recordAdd)
          } catch (e) {
            log('xhr add parse failed', e)
            reportFailure('cart add response unreadable (xhr)', {
              err: String((e && e.message) || e).slice(0, 200),
            })
          }
        })
      }
      return nativeSend.apply(this, arguments)
    }
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  function start() {
    // The page the shopper landed on starts the chain, if it names a watch.
    var first = document.querySelector('[data-upsell-nav]')
    if (first) {
      noteChainPage(
        first.getAttribute('data-upsell-nav-from'),
        first.getAttribute('data-upsell-nav-from-kind'),
        first.getAttribute('data-upsell-nav-from-models'),
      )
    }
    observeAll(document)
    if (mutationObserver) {
      mutationObserver.observe(document.body, { childList: true, subtree: true })
    }
    log('started', {
      visitorId: visitorId,
      surfaces: document.querySelectorAll('[data-upsell-surface]').length,
      navTiles: document.querySelectorAll('[data-upsell-nav]').length,
    })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true })
  } else {
    start()
  }

  // pagehide rather than unload: unload is unreliable on mobile Safari and
  // blocks the back/forward cache. visibilitychange covers the tab being
  // backgrounded on a phone, which is where most sessions actually end.
  window.addEventListener('pagehide', function () {
    flush(true)
  })
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flush(true)
  })
})()
